import { el } from "./dom.js";
import { loadBehavior, saveBehavior, loadSensitivity, saveSensitivity } from "../game/input.js";
import { forceHighGfx, setForceHighGfx } from "../game/renderer.js";

/**
 * FIRST LAUNCH (owner 2026-10-28: make the game friendly for someone who just
 * arrives). One modal, once per device: a three-control quick setup (mouse
 * sensitivity, field of view, graphics) and the two ways in — PLAY NOW (a
 * gentle bot match) or the guided training. Everything it sets is also in
 * Settings, so skipping loses nothing.
 */
const WELCOMED_KEY = "neonpurge.welcomed";

export function shouldWelcome(): boolean {
  try { return !localStorage.getItem(WELCOMED_KEY); } catch { return false; }
}

export function welcomeModal(actions: { onPlayNow: () => void; onTraining: () => void }): HTMLElement {
  const sens = loadSensitivity();
  const beh = loadBehavior();
  const done = (): void => {
    try { localStorage.setItem(WELCOMED_KEY, "1"); } catch { /* private mode */ }
    overlay.remove();
  };

  const row = (label: string, input: HTMLElement, val: HTMLElement): HTMLElement =>
    el("div", { class: "wl-row" }, el("label", { text: label }), input, val);

  // mouse sensitivity, shown as a ×1.0 multiple of the default 0.0023 rad/px
  const sensVal = el("span", { class: "wl-val" });
  const sensIn = el("input", { type: "range", min: "0.3", max: "3", step: "0.05", value: String(sens.mouse / 0.0023) }) as HTMLInputElement;
  const showSens = (): void => { sensVal.textContent = `×${Number(sensIn.value).toFixed(2)}`; };
  sensIn.oninput = () => { showSens(); saveSensitivity({ ...loadSensitivity(), mouse: 0.0023 * Number(sensIn.value) }); };
  showSens();

  const fovVal = el("span", { class: "wl-val" });
  const fovIn = el("input", { type: "range", min: "70", max: "115", step: "1", value: String(beh.fov ?? 90) }) as HTMLInputElement;
  const showFov = (): void => { fovVal.textContent = `${fovIn.value}°`; };
  fovIn.oninput = () => { showFov(); saveBehavior({ ...loadBehavior(), fov: Number(fovIn.value) }); };
  showFov();

  const gfx = el("select") as HTMLSelectElement;
  for (const [v, t] of [["auto", "Auto (adapts to your PC)"], ["full", "Full effects (strong PC)"]]) {
    const o = el("option", { value: v, text: t }) as HTMLOptionElement;
    if ((v === "full") === forceHighGfx()) o.selected = true;
    gfx.append(o);
  }
  gfx.onchange = () => setForceHighGfx(gfx.value === "full");

  const play = el("button", { class: "primary wl-play", text: "▶  PLAY NOW", "data-pad-start": "" });
  play.onclick = () => { done(); actions.onPlayNow(); };
  const train = el("button", { class: "wl-train", text: "🎓 2-minute training first" });
  train.onclick = () => { done(); actions.onTraining(); };
  const skip = el("button", { class: "ghost wl-skip", text: "Skip — take me to the menu" });
  skip.onclick = done;

  const overlay = el("div", { class: "welcome-overlay" },
    el("div", { class: "welcome-card" },
      el("div", { class: "wl-title", text: "WELCOME TO NEON PURGE" }),
      el("div", { class: "wl-sub", text: "A fast arena shooter. Every kill gives you a new gun — first to win with the knife takes the match." }),
      el("div", { class: "wl-h", text: "QUICK SETUP" }),
      row("Mouse sensitivity", sensIn, sensVal),
      row("Field of view", fovIn, fovVal),
      el("div", { class: "wl-row" }, el("label", { text: "Graphics" }), gfx, el("span", { class: "wl-val" })),
      el("div", { class: "wl-note", text: "You can change all of this later in Settings. In a match, hold H to see the controls." }),
      play, train, skip,
    ),
  );
  return overlay;
}
